import React from 'react'
import { Modal } from 'antd'
import {
  BookOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  LineChartOutlined,
  MailOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useSelector } from 'react-redux'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import {
  DEFAULT_PLAN_LIMIT_BENEFITS,
  DEFAULT_PLAN_LIMIT_NOTE,
  getDefaultPlanLimitCopy,
} from 'src/constants/planLimitCopy'
import GoodsReceiptingLimitUpsell from 'src/components/growthEngine/GoodsReceiptingLimitUpsell'
import {
  FREE_LIMIT_HELP_FIRST_BODY,
  FREE_LIMIT_HELP_FIRST_TITLE,
  shouldLeadFreeLimitWithHelp,
} from 'src/utils/growthEngineFreeLimitHelpFirst'
import { formatRecipePlanBenefit } from 'src/constants/recipePlanAllowances'

const StyledContainer = styled.div`
  padding: 4px 4px 0;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`

const UpgradeButton = styled.button`
  background-color: ${({ theme }) => theme.colours.avocadoGreen};
  color: white;
  font-size: 16px;
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colours.avocadoGreen};
  }
`

const UpsellHero = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f6ffed 0%, #fcfff5 100%);
  border: 1px solid #d9f7be;
  margin-bottom: 18px;
`

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #b5d922;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`

const HeroCopy = styled.div`
  min-width: 0;
`

const HeroTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #262626;
  line-height: 1.3;
`

const HeroText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #595959;
`

const PlanRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`

const PlanPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #d9d9d9;
  color: #434343;
  font-size: 13px;
  font-weight: 600;
`

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const BenefitCard = styled.div`
  padding: 14px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
`

const BenefitTitle = styled.p`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
`

const BenefitText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #8c8c8c;
`

const UpsellNote = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8c8c8c;
`

const TryFirst = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 12px;
  margin: 0 0 16px;
  padding: 16px;
`

const TryTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #262626;
  line-height: 1.3;
`

const TryBody = styled.p`
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.5;
  color: #595959;
`

const ReconciliationEmailUpsell = () => (
  <>
    <UpsellHero>
      <HeroIcon>
        <MailOutlined />
      </HeroIcon>
      <HeroCopy>
        <HeroTitle>Get reconciliation reminders by email</HeroTitle>
        <HeroText>
          Stay on top of outstanding orders without logging in every day. Weekly
          or monthly summaries go straight to your team.
        </HeroText>
      </HeroCopy>
    </UpsellHero>

    <PlanRow>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Starter
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Growth
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Pro
      </PlanPill>
    </PlanRow>

    <BenefitGrid>
      <BenefitCard>
        <BenefitTitle>What you get</BenefitTitle>
        <BenefitText>
          Outstanding orders, recommended actions, and a CSV attachment in each
          reminder.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Why it matters</BenefitTitle>
        <BenefitText>
          Keeps Spend over time and Gross Profit accurate when receipting slips
          behind.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Weekly</BenefitTitle>
        <BenefitText>Sent Monday morning for the previous week.</BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Monthly</BenefitTitle>
        <BenefitText>Sent on the first Monday of each month.</BenefitText>
      </BenefitCard>
    </BenefitGrid>

    <UpsellNote>
      On-page reconciliation tools stay available on OMS Lite. Starter, Growth,
      or Pro turn on automated email reminders.
    </UpsellNote>
  </>
)

const GpDataEntryUpsell = () => (
  <>
    <UpsellHero>
      <HeroIcon>
        <LineChartOutlined />
      </HeroIcon>
      <HeroCopy>
        <HeroTitle>Track real Gross Profit, not guesswork</HeroTitle>
        <HeroText>
          Enter sales, misc costs, and forecasts to see how your venue is
          performing day by day.
        </HeroText>
      </HeroCopy>
    </UpsellHero>

    <PlanRow>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Starter
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Growth
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Pro
      </PlanPill>
    </PlanRow>

    <BenefitGrid>
      <BenefitCard>
        <BenefitTitle>Enter revenue</BenefitTitle>
        <BenefitText>
          Record daily sales so GP reflects what actually came through the till.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Misc costs & forecasts</BenefitTitle>
        <BenefitText>
          Add one-off costs and forecast revenue to plan ahead with confidence.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Edit in the table</BenefitTitle>
        <BenefitText>
          Update figures inline as numbers change — no spreadsheet exports.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Accurate GP</BenefitTitle>
        <BenefitText>
          Works best with reconciled orders in Goods receipting for true costs.
        </BenefitText>
      </BenefitCard>
    </BenefitGrid>

    <UpsellNote>
      You can view Gross Profit on OMS Lite. Starter, Growth, or Pro let you
      enter and edit the data behind it.
    </UpsellNote>
  </>
)

const InvoiceUploadLimitUpsell = () => (
  <>
    <UpsellHero>
      <HeroIcon>
        <CloudUploadOutlined />
      </HeroIcon>
      <HeroCopy>
        <HeroTitle>Keep invoices flowing into matching</HeroTitle>
        <HeroText>
          You&apos;ve used the 5 uploads OMS Lite includes this month. Starter,
          Growth, and Pro add more — or unlimited on Pro — so invoices keep
          moving into matching and receipting.
        </HeroText>
      </HeroCopy>
    </UpsellHero>

    <PlanRow>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Starter
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Growth
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Pro
      </PlanPill>
    </PlanRow>

    <BenefitGrid>
      <BenefitCard>
        <BenefitTitle>More invoice uploads</BenefitTitle>
        <BenefitText>
          Starter 25/month · Growth 100/month · Pro unlimited.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Faster three-way matching</BenefitTitle>
        <BenefitText>
          Keep PO, goods receipt, and invoice work moving without waiting for
          next month&apos;s reset.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Automatic invoice reading</BenefitTitle>
        <BenefitText>
          Continue automatic reading and review where your plan includes it.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Procurement features</BenefitTitle>
        <BenefitText>
          Unlock additional operational tools as you move up the plan ladder.
        </BenefitText>
      </BenefitCard>
    </BenefitGrid>

    <UpsellNote>
      Monthly allowances: Lite 5 · Starter 25 · Growth 100 · Pro unlimited. Your
      Lite allowance resets each calendar month, or choose a plan to keep going
      now.
    </UpsellNote>
  </>
)

const RecipeCreateLimitUpsell = ({ plan }) => {
  const helpFirst = shouldLeadFreeLimitWithHelp(plan)

  return (
    <>
      {helpFirst ? (
        <TryFirst data-testid='free-limit-help-first'>
          <TryTitle>{FREE_LIMIT_HELP_FIRST_TITLE}</TryTitle>
          <TryBody>{FREE_LIMIT_HELP_FIRST_BODY.recipes}</TryBody>
          <GrowthEngineHumanDemoRow featureId='recipes' />
        </TryFirst>
      ) : (
        <UpsellHero>
          <HeroIcon>
            <BookOutlined />
          </HeroIcon>
          <HeroCopy>
            <HeroTitle>Keep costing more of your menu</HeroTitle>
            <HeroText>
              You&apos;re monitoring every recipe your plan includes. A higher
              plan lets you monitor more dishes. A recipe you save is still
              stored. It is not monitored until the plan has room.
            </HeroText>
          </HeroCopy>
        </UpsellHero>
      )}

      <PlanRow>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Starter
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Growth
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Pro
        </PlanPill>
      </PlanRow>

      <BenefitGrid>
        <BenefitCard>
          <BenefitTitle>{formatRecipePlanBenefit('Starter')}</BenefitTitle>
          <BenefitText>
            Monitor 50 recipes and store 200 on a single site.
          </BenefitText>
        </BenefitCard>
        <BenefitCard>
          <BenefitTitle>{formatRecipePlanBenefit('Growth')}</BenefitTitle>
          <BenefitText>
            Monitor 100 recipes and store as many as the menu needs.
          </BenefitText>
        </BenefitCard>
        <BenefitCard>
          <BenefitTitle>{formatRecipePlanBenefit('Pro')}</BenefitTitle>
          <BenefitText>
            Monitor and store without a cap, plus full menu costing tools.
          </BenefitText>
        </BenefitCard>
        <BenefitCard>
          <BenefitTitle>Why it matters</BenefitTitle>
          <BenefitText>
            Cost every dish, track margins, and keep recipes linked to your
            ordering.
          </BenefitText>
        </BenefitCard>
      </BenefitGrid>

      <UpsellNote>
        Monitored recipes get live costing and margin tracking. You can still
        store a recipe without monitoring it. If a downgrade left you over the
        monitor limit, stop monitoring the extras the list names — stored
        recipes stay yours.
      </UpsellNote>
    </>
  )
}

const RestrictedSupplierUpsell = () => (
  <>
    <UpsellHero>
      <HeroIcon>
        <SafetyCertificateOutlined />
      </HeroIcon>
      <HeroCopy>
        <HeroTitle>Unlock unlimited restricted suppliers</HeroTitle>
        <HeroText>
          Require manager approval before orders go to sensitive suppliers —
          without being capped at one on Lite.
        </HeroText>
      </HeroCopy>
    </UpsellHero>

    <PlanRow>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Starter
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Growth
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Pro
      </PlanPill>
    </PlanRow>

    <BenefitGrid>
      <BenefitCard>
        <BenefitTitle>Manager approval</BenefitTitle>
        <BenefitText>
          Orders to restricted suppliers wait for sign-off before they&apos;re
          sent.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Approval emails</BenefitTitle>
        <BenefitText>
          Notify managers by email so nothing slips through at busy times.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Control spend</BenefitTitle>
        <BenefitText>
          Gate high-value or discretionary suppliers without slowing everyday
          ordering.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Unlimited on paid plans</BenefitTitle>
        <BenefitText>
          Restrict as many suppliers as you need on Starter, Growth, or Pro.
        </BenefitText>
      </BenefitCard>
    </BenefitGrid>

    <UpsellNote>
      OMS Lite includes one restricted supplier. Upgrade to Starter or above for
      unlimited restricted suppliers.
    </UpsellNote>
  </>
)

const DefaultPlanLimitUpsell = ({ plan }) => {
  const { title, text } = getDefaultPlanLimitCopy(plan)

  return (
    <>
      <UpsellHero>
        <HeroIcon>
          <RiseOutlined />
        </HeroIcon>
        <HeroCopy>
          <HeroTitle>{title}</HeroTitle>
          <HeroText>{text}</HeroText>
        </HeroCopy>
      </UpsellHero>

      <PlanRow>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Starter
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Growth
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Pro
        </PlanPill>
      </PlanRow>

      <BenefitGrid>
        {DEFAULT_PLAN_LIMIT_BENEFITS.map(benefit => (
          <BenefitCard key={benefit.title}>
            <BenefitTitle>{benefit.title}</BenefitTitle>
            <BenefitText>{benefit.text}</BenefitText>
          </BenefitCard>
        ))}
      </BenefitGrid>

      <UpsellNote>{DEFAULT_PLAN_LIMIT_NOTE}</UpsellNote>
    </>
  )
}

const VARIANT_CONFIG = {
  reconciliationEmailReports: {
    modalTitle: 'Email reconciliation reminders',
    width: 620,
    content: <ReconciliationEmailUpsell />,
  },
  gpDataEntry: {
    modalTitle: 'Gross Profit data entry',
    width: 620,
    content: <GpDataEntryUpsell />,
  },
  invoiceUploadLimit: {
    modalTitle: 'More invoice uploads',
    width: 620,
    content: <InvoiceUploadLimitUpsell />,
  },
  recipeCreateLimit: {
    modalTitle: 'Monitor more recipes',
    width: 620,
  },
  restrictedSupplier: {
    modalTitle: 'Restricted suppliers',
    width: 620,
    content: <RestrictedSupplierUpsell />,
  },
  goodsReceiptingLimit: {
    modalTitle: 'Goods receipting',
    width: 620,
  },
}

const PlanLimitModal = ({
  showLimitModal,
  setShowLimitModal,
  plan,
  variant,
  copy,
  recommendation,
  guidedSuccess,
}) => {
  const history = useHistory()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const variantConfig = variant ? VARIANT_CONFIG[variant] : null
  const modalTitle = variantConfig?.modalTitle ?? 'Get more from OMS'
  const modalWidth = variantConfig?.width ?? 620
  const isGoodsReceiptingLimit = variant === 'goodsReceiptingLimit'
  const isRecipeCreateLimit = variant === 'recipeCreateLimit'
  const isRecipeHelpFirst =
    isRecipeCreateLimit && shouldLeadFreeLimitWithHelp(plan)

  return (
    <Modal
      title={modalTitle}
      centered
      open={true}
      width={modalWidth}
      footer={null}
      onCancel={() => {
        setShowLimitModal(false)
      }}
      visible={showLimitModal}
    >
      <StyledContainer>
        {isGoodsReceiptingLimit ? (
          <GoodsReceiptingLimitUpsell
            copy={copy}
            recommendation={recommendation}
            guidedSuccess={guidedSuccess}
            plan={plan}
          />
        ) : isRecipeCreateLimit ? (
          <RecipeCreateLimitUpsell plan={plan} />
        ) : variantConfig ? (
          variantConfig.content
        ) : (
          <DefaultPlanLimitUpsell plan={plan} />
        )}
        <Footer>
          <UpgradeButton
            onClick={() => {
              setShowLimitModal(false)
              history.push(`/v/${venueId}/subscriptions/oms-plans`)
            }}
          >
            <RiseOutlined style={{ marginRight: 8 }} />
            View plans
          </UpgradeButton>
        </Footer>
        {isGoodsReceiptingLimit || isRecipeHelpFirst ? null : (
          <GrowthEngineHumanDemoRow />
        )}
      </StyledContainer>
    </Modal>
  )
}

export default PlanLimitModal
